import { fnv1aHex } from '@tempo-io/string-util';
import { z } from 'zod';

// Field caps: this object is built by an LLM call and its lensPhrase in particular renders
// directly in UI (a header chip), so caps here bound a generation call's output size, same
// rationale as libs/common/sir-dto/src/agentic-insights/persona-dto.ts's PROMPT_FIELD_MAX.
//
// EMPHASIS_MAX and TONE_MAX are generous because the model tends to write full descriptive
// sentences for these two fields rather than short phrases. The interpretation prompt asks for
// conciseness, but a character-count instruction to an LLM is a soft ask, not a guarantee — these
// caps are the actual enforced backstop, so they need headroom above typical model verbosity.
const EMPHASIS_MAX = 400;
const TONE_MAX = 400;
const LENS_PHRASE_MAX = 120;
const METRIC_MAX = 80;
const MAX_METRICS = 10;

export const lensDetailLevelSchema = z.enum(['summary', 'balanced', 'detailed']);
export type LensDetailLevel = z.infer<typeof lensDetailLevelSchema>;

export const lensFramingSchema = z.object({
  emphasis: z.string().min(1).max(EMPHASIS_MAX),
  metricsToForeground: z.array(z.string().min(1).max(METRIC_MAX)).min(1).max(MAX_METRICS),
  detailLevel: lensDetailLevelSchema,
  tone: z.string().min(1).max(TONE_MAX),
  lensPhrase: z.string().min(1).max(LENS_PHRASE_MAX),
});

export type LensFraming = z.infer<typeof lensFramingSchema>;

// zod's z.toJSONSchema() serialization isn't guaranteed stable across versions, so this fingerprint
// is built from field names and the cap constants above instead — it still changes automatically
// whenever the schema actually changes, with no version number to remember to bump.
export const LENS_FRAMING_SCHEMA_FINGERPRINT = fnv1aHex(
  JSON.stringify({
    fields: Object.keys(lensFramingSchema.shape).sort(),
    emphasisMax: EMPHASIS_MAX,
    toneMax: TONE_MAX,
    lensPhraseMax: LENS_PHRASE_MAX,
    metricMax: METRIC_MAX,
    maxMetrics: MAX_METRICS,
    detailLevels: lensDetailLevelSchema.options,
  }),
);
